import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useFitHeight } from '../lib/fitHeight';
import { TopScrollbar } from '../components/TopScrollbar';
import {
  api, type AcctCorp, type InsAccountRow, type InsFileItem, type InsFilePreview, type InsInstallmentRow, type InsPolicyRow, type InsPreviewRow, type InsRowInput,
} from '../lib/api';
import { ErrorBox, commaInput, dash, money } from '../components/DataTable';
import Modal from '../components/Modal';
import { canScreen, useMe } from '../lib/me';
import { useAcctCorp } from '../lib/sticky';

/**
 * 회계 「보험」(개선요청 #135, 2026-10-06 회계팀) — 공제조합 차량보험 갱신·신규 → 4회 분납 → 출납일보 「출납반영」.
 * 서버 `functions/src/insurance.ts` · 분납 계산은 `insuranceCalc.ts` 하나(화면은 미리보기를 받아 그린다 — 계산을 베끼지 않는다).
 *
 * - 「계약 입력」 = 시트처럼 칸을 붙인 여러 줄 표(#135 2차 — 회계팀 시트의 노란 칸 6개를 앞에, Enter·↑↓ 로 줄 이동, 분납은 한 칸).
 *   엑셀에서 복사한 칸(탭·줄바꿈)을 아무 칸에 붙여 넣으면 그 칸부터 채운다(기간 「~」 칸은 뺀다).
 *   차량번호를 넣으면 공제조합(번호판 앞 「서울」 → 서울공제조합)·법인·기사명(시작일의 편성)을 빈 칸에만 채우고 납부 계좌를 보인다.
 * - 목록에서 회차를 골라 「출납반영」 = 출납일보에 기한 − 7일로 한 줄씩(받는 계좌 = 「납부 계좌」 법인 × 공제조합).
 * 🔴 권한은 출납일보와 같다(회계 화면 조회/입력) — 서버가 막는다.
 */

const UNIONS = ['경기공제조합', '서울공제조합'] as const;
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const kstToday = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const short = (ymd: string | null | undefined) => (ymd ? ymd.slice(2) : '—');

/** 연도 없는 월·일 → 오늘에서 반년 안쪽의 해(12월에 「0105」를 치면 이듬해). */
function nearYear(mo: string, d: string): string {
  const today = kstToday();
  const y = Number(today.slice(0, 4));
  const at = (yy: number) => `${yy}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  const days = (iso: string) => (Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000;
  if (days(at(y)) < -183) return String(y + 1);
  if (days(at(y)) > 183) return String(y - 1);
  return String(y);
}

/**
 * 「2026-10-29」·「2026.10.29」·「26.10.29」·「20261029」·「10/29」·「1029」(#135 — 시트의 공제기간 칸 그대로) → YYYY-MM-DD.
 * 연도가 없으면 오늘에서 반년 안쪽의 해. 「26/10/01~27/10/01」처럼 기간이면 앞 날짜. 못 읽으면 글자 그대로.
 */
export function normDate(v: string): string {
  const s = v.trim().replace(/\s+/g, '').replace(/\.$/, '').split('~')[0]!;
  if (!s) return '';
  let m = /^(\d{4})[-./](\d{1,2})[-./](\d{1,2})$/.exec(s) ?? /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  let y: string | undefined; let mo: string | undefined; let d: string | undefined;
  if (m) [, y, mo, d] = m;
  else if ((m = /^(\d{2})[-./](\d{1,2})[-./](\d{1,2})$/.exec(s))) { y = `20${m[1]}`; mo = m[2]; d = m[3]; }
  else if ((m = /^(\d{1,2})[/.-](\d{1,2})$/.exec(s)) || (m = /^(\d{1,2})(\d{2})$/.exec(s))) { mo = m[1]; d = m[2]; y = nearYear(mo!, d!); }
  else return v.trim();
  const iso = `${y}-${mo!.padStart(2, '0')}-${d!.padStart(2, '0')}`;
  const t = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === iso ? iso : v.trim();
}
const digits = (v: string) => v.replace(/[^0-9]/g, '');

// ─────────────────────────────────────────────────────────────────────────────
// 계약 입력(엑셀처럼 여러 줄)
// ─────────────────────────────────────────────────────────────────────────────

interface GRow {
  key: number; corpId: string; unionName: string; carNo: string; driverName: string; kind: '갱신' | '신규'; startDate: string;
  total: string; first: string; ownDamage: string;
  /** 제안을 채운 (차량|시작일) — 같은 키로는 다시 안 채운다(사람이 지운 값을 되살리지 않게). */
  filledKey?: string;
  unionTouched?: boolean;
}
/**
 * 칸 순서 = 회계팀 시트의 노란 칸 순서(#135 2차 — 공제기간·차량번호·기사·총금액·1회차 금액·자차) → 나머지는 뒤에(자동으로 채워진다).
 * 앞 6칸(INPUT_COLS)만 치면 되고, Enter 는 6번째 칸에서 다음 줄 첫 칸으로 간다.
 */
const COLS = ['공제시작', '차량번호', '기사', '총금액', '1회차 금액', '자차', '구분', '법인', '공제조합'] as const;
const INPUT_COLS = 6;
let rowSeq = 0;
const blank = (): GRow => ({ key: ++rowSeq, corpId: '', unionName: '', carNo: '', driverName: '', kind: '갱신', startDate: '', total: '', first: '', ownDamage: '' });
const isEmpty = (r: GRow) => !r.carNo && !r.total && !r.first && !r.startDate && !r.driverName;
const plateUnion = (car: string) => (car.trim().startsWith('서울') ? '서울공제조합' : '경기공제조합');

function corpFrom(text: string, corps: AcctCorp[]): string {
  const t = text.trim();
  if (!t) return '';
  const hit = corps.find((c) => c.short_name === t) ?? corps.find((c) => c.name === t)
    ?? corps.find((c) => t.includes(c.short_name) && !c.parent_corp_id) ?? corps.find((c) => c.name.includes(t));
  return hit ? String(hit.corp_id) : '';
}

/** 붙여 넣은 한 칸을 그 열의 값으로. */
function setCell(r: GRow, col: number, raw: string, corps: AcctCorp[]): GRow {
  const v = raw.trim();
  switch (col) {
    case 0: return { ...r, startDate: normDate(v) };
    case 1: return { ...r, carNo: v };
    case 2: return { ...r, driverName: v };
    case 3: return { ...r, total: digits(v) };
    case 4: return { ...r, first: digits(v) };
    case 5: return { ...r, ownDamage: ownPct(v) };
    case 6: return { ...r, kind: v.includes('신규') ? '신규' : '갱신' };
    case 7: return { ...r, corpId: corpFrom(v, corps) };
    case 8: return { ...r, unionName: v.includes('서울') ? '서울공제조합' : v.includes('경기') ? '경기공제조합' : r.unionName, unionTouched: !!v };
    default: return r;
  }
}

/** 자차 칸 — 시트엔 숫자만(0·60) 있다 → 「60%」(적요 끝 `_자차60%` 모양). 숫자가 아니면 그대로. */
const ownPct = (v: string) => (/^[0-9]+$/.test(v.trim()) ? `${v.trim()}%` : v.trim());

/**
 * 붙여 넣은 한 줄 정리(#135 2차) — 시트에서 노란 칸을 한 번에 복사하면 사이에 낀 「1회차기간」(10/01~12/01)·앞의 「계약기간」(26/10/01~27/10/01)
 * 이 같이 온다. 기간(「~」)은 넣을 칸이 아니라 뺀다. 단 첫 칸부터 붙였는데 남은 첫 값이 날짜가 아니면 계약기간의 앞 날짜를 시작일로 쓴다.
 */
function pasteCells(cells: string[], col: number): string[] {
  const kept = cells.filter((c) => !c.includes('~'));
  if (col === 0 && kept.length && !/^[0-9]/.test(kept[0]!.trim())) {
    const period = cells.find((c) => c.includes('~'));
    if (period) return [period, ...kept];
  }
  return kept;
}

function InsEntryForm({ corps, onClose, onSaved }: { corps: AcctCorp[]; onClose: () => void; onSaved: (n: number) => void }) {
  const [rows, setRows] = useState<GRow[]>(() => [blank(), blank(), blank()]);
  const [pv, setPv] = useState<Map<number, InsPreviewRow>>(new Map());
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const live = useMemo(() => rows.filter((r) => !isEmpty(r)), [rows]);

  const toInput = (r: GRow): InsRowInput => ({
    corpId: r.corpId ? Number(r.corpId) : null, unionName: r.unionName || (r.carNo ? plateUnion(r.carNo) : ''), carNo: r.carNo.trim(),
    driverName: r.driverName.trim(), kind: r.kind, startDate: r.startDate, totalAmount: r.total, firstAmount: r.first, ownDamage: r.ownDamage.trim(),
  });

  // 미리보기 — 치는 동안 0.4초 쉬면 한 번(같은 내용이면 안 부른다). 제안은 빈 칸에만, (차량|시작일)마다 한 번.
  const lastKey = useRef('');
  useEffect(() => {
    const payload = live.map(toInput);
    const k = JSON.stringify(payload);
    if (k === lastKey.current) return;
    const t = setTimeout(() => {
      lastKey.current = k;
      if (payload.length === 0) { setPv(new Map()); return; }
      const keys = live.map((r) => r.key);
      api.previewInsurance({ rows: payload }).then((res) => {
        const m = new Map<number, InsPreviewRow>();
        res.rows.forEach((p, i) => m.set(keys[i]!, p));
        setPv(m);
        setRows((cur) => cur.map((r) => {
          const p = m.get(r.key);
          if (!p) return r;
          const fk = `${r.carNo.trim()}|${r.startDate}`;
          let next = r;
          if (!r.unionTouched && p.suggest.unionName && r.unionName !== p.suggest.unionName) next = { ...next, unionName: p.suggest.unionName };
          if (r.filledKey !== fk && r.carNo.trim() && /^\d{4}-\d{2}-\d{2}$/.test(r.startDate)) {
            next = {
              ...next, filledKey: fk,
              corpId: next.corpId || (p.suggest.corpId ? String(p.suggest.corpId) : ''),
              driverName: next.driverName || p.suggest.driverName || '',
            };
          }
          return next;
        }));
      }).catch((e) => setErr(errMsg(e)));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  // 납부 계좌(법인 × 공제조합) — 줄마다 어느 계좌로 나가는지 바로 보이게(#135 2차 「조합명·계좌번호 자동」).
  const [accounts, setAccounts] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    api.listInsAccounts().then((r) => setAccounts(new Map(r.rows.map((a) => [`${a.corp_id}|${a.union_name}`, a.account])))).catch(() => undefined);
  }, []);

  // 엑셀처럼 움직이기 — Enter = 아래 줄(마지막 입력 칸 「자차」에서는 다음 줄 첫 칸) · ↑↓ = 위아래 줄 같은 칸.
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const focusCell = (row: number, col: number) => requestAnimationFrame(() => {
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-cell="${row}-${col}"]`);
    el?.focus();
    if (el instanceof HTMLInputElement) el.select();
  });
  const onKey = (row: number, col: number) => (e: KeyboardEvent<HTMLElement>) => {
    if (e.nativeEvent.isComposing) return; // 한글 조합 중 Enter 는 글자 확정이다
    const isSelect = e.currentTarget instanceof HTMLSelectElement;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (row + 1 >= rows.length) setRows((cur) => [...cur, blank()]);
      focusCell(row + 1, col === INPUT_COLS - 1 ? 0 : col);
    } else if (!isSelect && e.key === 'ArrowDown' && row + 1 < rows.length) { e.preventDefault(); focusCell(row + 1, col); }
    else if (!isSelect && e.key === 'ArrowUp' && row > 0) { e.preventDefault(); focusCell(row - 1, col); }
  };

  const upd = (key: number, patch: Partial<GRow>) => setRows((cur) => {
    const next = cur.map((r) => (r.key === key ? { ...r, ...patch } : r));
    return next[next.length - 1] && !isEmpty(next[next.length - 1]!) ? [...next, blank()] : next;
  });

  /** 엑셀에서 복사한 칸 — 누른 칸부터 오른쪽·아래로 채운다. 머리줄(차량번호 칸에 숫자 없음)은 건너뛴다. */
  const onPaste = (rowIdx: number, col: number) => (e: ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData.getData('text/plain');
    if (!/[\t\n]/.test(text.replace(/\n$/, ''))) return;
    e.preventDefault();
    const lines = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n').map((l) => pasteCells(l.split('\t'), col));
    const carAt = 1 - col;
    const body = lines.filter((cells) => !(carAt >= 0 && carAt < cells.length && !/[0-9]/.test(cells[carAt] ?? '')) && cells.some((c) => c.trim()));
    setRows((cur) => {
      const next = [...cur];
      body.forEach((cells, i) => {
        const at = rowIdx + i;
        while (next.length <= at) next.push(blank());
        let r = next[at]!;
        cells.forEach((c, j) => { if (col + j < COLS.length) r = setCell(r, col + j, c, corps); });
        next[at] = r;
      });
      if (!isEmpty(next[next.length - 1]!)) next.push(blank());
      return next;
    });
  };

  const save = async () => {
    setErr('');
    if (live.length === 0) { setErr('넣을 줄이 없습니다.'); return; }
    const badRow = live.findIndex((r) => pv.get(r.key)?.error || pv.get(r.key)?.duplicate);
    if (badRow >= 0) { setErr(`${badRow + 1}번째 줄을 확인하세요: ${pv.get(live[badRow]!.key)?.error ?? '이미 있는 계약입니다.'}`); return; }
    setBusy(true);
    try {
      const r = await api.saveInsurance({ rows: live.map(toInput) });
      onSaved(r.added);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  // 시트처럼 칸을 붙인 표 — 입력 칸은 테두리 없이 칸을 꽉 채운다(칸 사이 이동 = Tab·Enter·↑↓).
  const cell = 'h-8 w-full border-0 bg-transparent px-1.5 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-inset focus:ring-brand-400';
  const td = 'border border-line p-0';
  const nav = (i: number, c: number) => ({ 'data-cell': `${i}-${c}`, onKeyDown: onKey(i, c), onPaste: onPaste(i, c) });
  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">보험 계약 입력</h2>
        <span className="text-xs text-ink-soft">
          <b className="rounded bg-amber-100 px-1">노란 칸</b>만 치시면 됩니다 — 공제시작은 1001 처럼 월일 네 자리, 자차는 숫자만(60). Enter 를 누르면 아래 줄로 갑니다.
          차량번호를 넣으면 공제조합·법인·기사·납부 계좌는 <b>빈 칸에만</b> 자동으로 채웁니다. 시트에서 여러 줄을 복사해 첫 칸에 붙여 넣어도 됩니다.
        </span>
      </div>
      {err && <ErrorBox message={err} />}
      <div className="max-h-[calc(100vh-14rem)] overflow-auto">
        <table className="w-full min-w-[1250px] border-collapse text-sm [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-surface">
          <thead>
            <tr className="text-left text-xs text-ink-soft">
              <th className="w-8 border border-line px-1">#</th>
              {COLS.map((c, j) => <th key={c} className={`border border-line px-1.5 py-1 font-medium ${j < INPUT_COLS ? '!bg-amber-100 text-ink' : ''}`}>{c}</th>)}
              <th className="border border-line px-1.5 font-medium">납부 계좌</th>
              <th className="border border-line px-1.5 font-medium">분납(출납 = 기한 7일 전)</th>
              <th className="w-8 border border-line" />
            </tr>
          </thead>
          <tbody ref={bodyRef}>
            {rows.map((r, i) => {
              const p = pv.get(r.key);
              const corpId = r.corpId || (p?.suggest.corpId ? String(p.suggest.corpId) : '');
              const union = r.unionName || p?.suggest.unionName || (r.carNo.trim() ? plateUnion(r.carNo) : '');
              const account = corpId && union ? accounts.get(`${corpId}|${union}`) : undefined;
              const sched = p?.schedule?.map((s) => `${s.seq}회 ${short(s.dueDate)} ${money(s.amount)}`).join(' · ') ?? '';
              return (
                <tr key={r.key} className="align-middle">
                  <td className={`${td} px-1 text-center text-xs text-ink-faint`}>{i + 1}</td>
                  <td className={`${td} w-24`}><input className={cell} value={r.startDate} placeholder="1001" onChange={(e) => upd(r.key, { startDate: e.target.value })}
                                                     onBlur={(e) => upd(r.key, { startDate: normDate(e.target.value) })} aria-label="공제시작" {...nav(i, 0)} /></td>
                  <td className={`${td} w-32`}><input className={cell} value={r.carNo} placeholder="경기76아6814" onChange={(e) => upd(r.key, { carNo: e.target.value })} aria-label="차량번호" {...nav(i, 1)} /></td>
                  <td className={`${td} w-24`}><input className={cell} value={r.driverName} placeholder="기사·직영" onChange={(e) => upd(r.key, { driverName: e.target.value })} aria-label="기사" {...nav(i, 2)} /></td>
                  <td className={`${td} w-28`}><input className={`${cell} text-right tabular-nums`} inputMode="numeric" value={commaInput(r.total)} onChange={(e) => upd(r.key, { total: digits(e.target.value) })} aria-label="총금액" {...nav(i, 3)} /></td>
                  <td className={`${td} w-28`}><input className={`${cell} text-right tabular-nums`} inputMode="numeric" value={commaInput(r.first)} onChange={(e) => upd(r.key, { first: digits(e.target.value) })} aria-label="1회차 금액" {...nav(i, 4)} /></td>
                  <td className={`${td} w-16`}><input className={cell} value={r.ownDamage} placeholder="60" onChange={(e) => upd(r.key, { ownDamage: e.target.value })}
                                                     onBlur={(e) => upd(r.key, { ownDamage: ownPct(e.target.value) })} aria-label="자차" {...nav(i, 5)} /></td>
                  <td className={`${td} w-20`}>
                    <select className={cell} value={r.kind} onChange={(e) => upd(r.key, { kind: e.target.value as GRow['kind'] })} aria-label="구분" tabIndex={-1} {...nav(i, 6)}>
                      <option value="갱신">갱신</option><option value="신규">신규</option>
                    </select>
                  </td>
                  <td className={`${td} w-24`}>
                    <select className={cell} value={r.corpId} onChange={(e) => upd(r.key, { corpId: e.target.value })} aria-label="법인" tabIndex={-1} {...nav(i, 7)}>
                      <option value="">(자동)</option>
                      {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
                    </select>
                  </td>
                  <td className={`${td} w-28`}>
                    <select className={cell} value={r.unionName} onChange={(e) => upd(r.key, { unionName: e.target.value, unionTouched: true })} aria-label="공제조합" tabIndex={-1} {...nav(i, 8)}>
                      <option value="">{r.carNo.trim() ? `(자동) ${union.replace('공제조합', '')}` : '(자동)'}</option>
                      {UNIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </td>
                  <td className={`${td} whitespace-nowrap px-1.5 text-xs tabular-nums ${account ? '' : 'text-ink-faint'}`}>
                    {account ?? (r.carNo.trim() ? (corpId ? '계좌 없음 — 「납부 계좌」에서 넣기' : '법인 확인 필요') : '')}
                  </td>
                  <td className={`${td} max-w-[24rem] truncate px-1.5 text-xs`}
                      title={p?.schedule?.map((s) => `${s.seq}회 기한 ${s.dueDate} · ${money(s.amount)} · 출납 ${s.payDate}\n${s.summary}`).join('\n') ?? ''}>
                    {isEmpty(r) ? '' : !p ? <span className="text-ink-faint">계산 중…</span>
                      : p.duplicate ? <b className="text-red-700">이미 있는 계약(같은 차·시작일)</b>
                      : p.error ? <span className="text-red-700">{p.error}</span>
                      : <>
                          <span className="tabular-nums">{sched}</span>
                          {!p.suggest.allocLabel && r.carNo && /^\d{4}-\d{2}-\d{2}$/.test(r.startDate) && <span className="ml-2 text-amber-800">편성 못 찾음 — 법인·기사 확인</span>}
                        </>}
                  </td>
                  <td className={`${td} text-center`}>
                    <button type="button" className="px-1 text-ink-faint hover:text-red-700" title="이 줄 빼기" tabIndex={-1}
                            onClick={() => setRows((cur) => (cur.length > 1 ? cur.filter((x) => x.key !== r.key) : [blank()]))}>✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-ghost" onClick={() => setRows((cur) => [...cur, blank()])}>줄 더하기</button>
        <span className="text-xs text-ink-soft">{live.length}건 · 저장하면 1회차가 만들어집니다 — 2~4회차는 「분납자료 올리기」로 같은 계약에 붙습니다(출납일보는 목록에서 「출납반영」).</span>
        <div className="ml-auto flex gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>닫기</button>
          <button type="button" className="btn-primary" onClick={() => void save()} disabled={busy || live.length === 0}>{busy ? '저장 중…' : `${live.length}건 저장`}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 납부 계좌(법인 × 공제조합)
// ─────────────────────────────────────────────────────────────────────────────

function InsAccountPanel({ corps, canWrite }: { corps: AcctCorp[]; canWrite: boolean }) {
  const [rows, setRows] = useState<InsAccountRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await api.listInsAccounts();
      setRows(r.rows);
      setDraft(Object.fromEntries(r.rows.map((a) => [`${a.corp_id}|${a.union_name}`, a.account])));
    } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const main = corps.filter((c) => !c.parent_corp_id);
  const save = async (corpId: number, unionName: string) => {
    const k = `${corpId}|${unionName}`;
    const before = rows.find((a) => a.corp_id === corpId && a.union_name === unionName)?.account ?? '';
    const now = (draft[k] ?? '').trim();
    if (now === before) return;
    setErr(''); setSaved('');
    try {
      await api.saveInsAccount({ corpId, unionName, account: now });
      setSaved(`${main.find((c) => c.corp_id === corpId)?.short_name} × ${unionName} ${now ? '저장' : '지움'}`);
      await load();
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div className="card space-y-2 p-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-2">
        <b>납부 계좌</b>
        <span className="text-xs text-ink-soft">출납반영 때 출납일보 memo 칸에 들어갑니다. 칸을 벗어나면 저장 · 비우면 지움.</span>
        {saved && <span className="text-xs text-green-700">✓ {saved}</span>}
      </div>
      {err && <ErrorBox message={err} />}
      <table className="text-sm">
        <thead><tr className="text-left text-xs text-ink-soft"><th className="pr-3">법인</th>{UNIONS.map((u) => <th key={u} className="px-1 font-medium">{u}</th>)}</tr></thead>
        <tbody>
          {main.map((c) => (
            <tr key={c.corp_id}>
              <td className="pr-3">{c.short_name}</td>
              {UNIONS.map((u) => {
                const k = `${c.corp_id}|${u}`;
                return (
                  <td key={u} className="p-0.5">
                    <input className="field h-8 w-56 py-0 text-sm" value={draft[k] ?? ''} disabled={!canWrite} placeholder="—" aria-label={`${c.short_name} ${u} 계좌`}
                           onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                           onBlur={() => void save(c.corp_id, u)}
                           onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 분납자료 올리기(#135 2단계) — 경기공제조합 「분할납입 대상차량조회」 엑셀
// ─────────────────────────────────────────────────────────────────────────────

const ACTION_LABEL: Record<InsFileItem['action'], string> = {
  new: '새 계약', add: '회차 더함', update: '맞춤', same: '그대로', warn: '경고', error: '못 넣음',
};
const ACTION_TONE: Record<InsFileItem['action'], string> = {
  new: 'text-brand-700', add: 'text-brand-700', update: 'text-green-700', same: 'text-ink-faint', warn: 'text-amber-800', error: 'text-red-700',
};

function InsFileForm({ corps, onClose, onDone }: { corps: AcctCorp[]; onClose: () => void; onDone: (msg: string) => void }) {
  const [name, setName] = useState('');
  const [sheet, setSheet] = useState<unknown[][] | null>(null);
  const [fallback, setFallback] = useState('');
  const [pv, setPv] = useState<InsFilePreview | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);

  const preview = useCallback(async (rows: unknown[][], fb: string) => {
    setBusy(true); setErr('');
    try { setPv(await api.previewInsFile({ sheet: rows, fallbackCorpId: fb ? Number(fb) : null })); } catch (e) { setErr(errMsg(e)); setPv(null); } finally { setBusy(false); }
  }, []);

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setErr(''); setPv(null); setName(f.name);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]!];
      if (!ws) throw new Error('시트가 없습니다.');
      // raw = 날짜는 엑셀 일련값 그대로(서버가 날짜로 바꾼다 — 브라우저 Date 변환은 시간대에 따라 하루가 밀린다).
      const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: null });
      setSheet(rows);
      await preview(rows, fallback);
    } catch (e) { setErr(errMsg(e)); }
  };

  const save = async () => {
    if (!sheet || !pv?.counts) return;
    const c = pv.counts;
    if (!window.confirm(`올립니다 — 새 계약 ${c.newPolicies} · 회차 추가 ${c.newInstallments} · 맞춘 회차 ${c.matched} · 출납에 이미 있음(연결) ${c.inCash} · 경고 ${c.warnings}(건드리지 않음).\n계속할까요?`)) return;
    setBusy(true); setErr('');
    try {
      const r = await api.importInsFile({ sheet, fallbackCorpId: fallback ? Number(fallback) : null });
      const k = r.counts;
      onDone(`분납자료 올림 — 새 계약 ${k.newPolicies} · 회차 추가 ${k.newInstallments} · 맞춘 회차 ${k.matched} · 출납 연결 ${k.inCash} · 경고 ${k.warnings}`);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  const items = pv?.items ?? [];
  const shown = onlyIssues ? items.filter((i) => i.action !== 'same') : items;
  const c = pv?.counts;
  const blocked = !c || c.errors > 0 || (pv?.errors.length ?? 0) > 0;
  return (
    <div className="card space-y-3 p-4 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">분납자료 올리기</h2>
        <span className="text-xs text-ink-soft">
          경기공제조합 「분할납입 대상차량조회」 엑셀(첫 시트). 줄마다 계약번호 → 같은 차·회차·분납일자(±20일)로 계약을 찾아 분납일자·실효예정일(= 기한)·금액을 맞춥니다.
          없는 계약은 「분납자료」 계약으로 새로 만듭니다(법인·기사 = 분납일자의 편성). 출납일보에 이미 손으로 넣은 같은 줄은 <b>연결만</b> 합니다.
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".xlsx,.xls" aria-label="분납자료 파일" onChange={(e) => void onFile(e.target.files?.[0])} />
        {name && <span className="text-xs text-ink-faint">{name}</span>}
        <span className="text-xs">법인 못 찾을 때</span>
        <select className="field h-8 w-32 py-0 text-sm" value={fallback} aria-label="법인 못 찾을 때"
                onChange={(e) => { setFallback(e.target.value); if (sheet) void preview(sheet, e.target.value); }}>
          <option value="">(고르지 않음)</option>
          {corps.filter((x) => !x.parent_corp_id).map((x) => <option key={x.corp_id} value={x.corp_id}>{x.short_name}</option>)}
        </select>
      </div>
      {err && <ErrorBox message={err} />}
      {pv && pv.errors.length > 0 && (
        <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">{pv.errors.slice(0, 10).map((m) => <div key={m}>{m}</div>)}</div>
      )}
      {c && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>파일 {c.rows}줄{pv!.skipped ? ` (합계 줄 ${pv!.skipped} 건너뜀)` : ''}</span>
          <b className="text-brand-700">새 계약 {c.newPolicies}</b>
          <b className="text-green-700">맞춘 회차 {c.matched}</b>
          <b>이미 출납에 있음 {c.inCash}</b>
          <b className={c.warnings ? 'text-amber-800' : ''}>경고 {c.warnings}</b>
          <span className="text-ink-soft">회차 추가 {c.newInstallments} · 그대로 {c.same}</span>
          {c.errors > 0 && <b className="text-red-700">못 넣음 {c.errors}</b>}
          <span className="ml-auto inline-flex items-center gap-1 text-xs">
            <input id="ins-file-issues" type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
            <label htmlFor="ins-file-issues">「그대로」 숨기기</label>
          </span>
        </div>
      )}
      {items.length > 0 && (
        <div className="max-h-[50vh] overflow-auto rounded border border-line">
          <table className="w-full min-w-[980px] text-xs">
            <thead className="sticky top-0 bg-canvas text-left text-ink-soft">
              <tr><th className="px-2 py-1">행</th><th className="px-2">처리</th><th className="px-2">계약번호</th><th className="px-2">차량</th><th className="px-2">회차</th>
                <th className="px-2 text-right">금액</th><th className="px-2">분납일자</th><th className="px-2">실효예정일(기한)</th><th className="px-2">법인 · 기사</th><th className="px-2">출납 · 비고</th></tr>
            </thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.line} className="border-t border-line/50 align-top">
                  <td className="px-2 py-1 text-ink-faint">{i.line}</td>
                  <td className={`px-2 font-semibold ${ACTION_TONE[i.action]}`}>{ACTION_LABEL[i.action]}</td>
                  <td className="px-2">{dash(i.contractNo)}</td>
                  <td className="px-2">{i.carNo}</td>
                  <td className="px-2">4/{i.seq}</td>
                  <td className="px-2 text-right tabular-nums">{money(i.amount)}{i.before && i.before.amount !== i.amount && <div className="text-ink-faint">← {money(i.before.amount)}</div>}</td>
                  <td className="px-2 tabular-nums">{i.periodFrom}{i.before && i.before.periodFrom !== i.periodFrom && <div className="text-ink-faint">← {i.before.periodFrom}</div>}</td>
                  <td className="px-2 tabular-nums">{i.dueDate}{i.before && i.before.dueDate !== i.dueDate && <div className="text-ink-faint">← {i.before.dueDate}</div>}</td>
                  <td className="px-2">{dash(i.corpName)} · {i.unionName.replace('공제조합', '')} · {dash(i.driverName)}</td>
                  <td className="px-2">
                    {i.cashMatchId && <b>출납에 이미 있음 ({short(i.cashPayDate)})</b>}
                    {i.reflected && !i.cashMatchId && <span className="text-ink-faint">반영됨</span>}
                    {i.message && <div className={i.action === 'error' ? 'text-red-700' : i.action === 'warn' ? 'text-amber-800' : 'text-ink-soft'}>{i.message}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-soft">못 넣는 줄이 하나라도 있으면 하나도 안 올립니다. 같은 파일을 다시 올려도 바뀌는 것은 없습니다.</span>
        <div className="ml-auto flex gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>닫기</button>
          <button type="button" className="btn-primary" onClick={() => void save()} disabled={busy || blocked}>{busy ? '처리 중…' : '올리기'}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 목록 · 출납반영
// ─────────────────────────────────────────────────────────────────────────────

function statusOf(i: InsInstallmentRow) {
  if (i.reflected && i.cash_linked) return <span className="text-green-700" title="회계팀이 출납일보에 이미 넣어 둔 줄에 연결했습니다(새 줄을 만들지 않음)">출납에 이미 있음 · {short(i.cash_pay_date)}</span>;
  if (i.reflected) return <span className="text-green-700">반영 · 출납 {short(i.cash_pay_date)}</span>;
  if (i.cash_removed) return <span className="text-amber-800" title="출납일보에서 그 줄을 지웠습니다 — 다시 반영할 수 있습니다">출납에서 지움</span>;
  return <span className="text-ink-faint">미반영</span>;
}

export function InsuranceTab({ corps }: { corps: AcctCorp[] }) {
  const fitRef = useRef<HTMLDivElement>(null);
  const fitH = useFitHeight(fitRef);
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [corpId, setCorpId] = useAcctCorp();
  const [month, setMonth] = useState('');
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [pending, setPending] = useState(false);
  const [rows, setRows] = useState<InsPolicyRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [entry, setEntry] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [showAcct, setShowAcct] = useState(false);
  const [sel, setSel] = useState<Set<number>>(new Set());
  /** #204 — 출납반영 일자 직접(비우면 기한 − 7일). */
  const [payDate, setPayDate] = useState('');
  const [edit, setEdit] = useState<{ id: number; value: string } | null>(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await api.listInsurance({ month: month || null, corpId, q: q || null, pending });
      setRows(r.rows);
      setTruncated(r.truncated);
      const open = new Set(r.rows.flatMap((p) => p.installments.filter((i) => !i.reflected).map((i) => i.id)));
      setSel((cur) => new Set([...cur].filter((id) => open.has(id))));
    } catch (e) { setErr(errMsg(e)); }
  }, [month, corpId, q, pending]);
  useEffect(() => { void load(); }, [load]);

  const openInst = useMemo(() => rows.flatMap((p) => p.installments.filter((i) => !i.reflected)), [rows]);
  const picked = useMemo(() => openInst.filter((i) => sel.has(i.id)), [openInst, sel]);
  const pickedSum = useMemo(() => picked.reduce((s, i) => s + Number(i.amount), 0), [picked]);
  const allPicked = openInst.length > 0 && picked.length === openInst.length;

  const act = async (fn: () => Promise<string | void>) => {
    setBusy(true); setErr(''); setNote('');
    try { const m = await fn(); if (m) setNote(m); await load(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const reflect = (ids: { installmentIds?: number[]; policyIds?: number[] }, n: number, sum: number) => {
    if (n === 0) return;
    const when = payDate ? `일자 = ${payDate}(직접 고름)` : '일자 = 각 회차 기한 − 7일';
    if (!window.confirm(`출납일보에 ${n}줄(합계 ${money(sum)}원)을 넣습니다.\n${when} · 받는 계좌 = 납부 계좌.\n계속할까요?`)) return;
    void act(async () => {
      const r = await api.reflectInsurance({ ...ids, payDate: payDate || null });
      setSel(new Set());
      return `출납일보에 ${r.created}줄 넣었습니다${r.linked ? ` · 출납에 이미 있던 ${r.linked}줄은 연결만` : ''}${r.skipped ? ` · 이미 반영된 ${r.skipped}개는 건너뜀` : ''}.`;
    });
  };
  const saveAmount = (id: number, value: string) => {
    setEdit(null);
    const v = value.replace(/[^0-9]/g, '');
    const cur = openInst.find((i) => i.id === id);
    if (!v || !cur || Number(v) === Number(cur.amount)) return;
    void act(async () => { await api.updateInsInstallment({ id, amount: v }); return '회차 금액을 고쳤습니다(총금액도 회차 합으로 맞춤).'; });
  };

  return (
    <div className="space-y-2">
      <div className="btn-row-sm card flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
        <b>공제조합 보험 분납</b>
        <input type="month" className="field w-40" value={month} onChange={(e) => setMonth(e.target.value)} aria-label="기한 월" title="그 달에 기한이 있는 회차가 있는 계약만" />
        {month && <button className="btn-ghost text-xs" onClick={() => setMonth('')}>월 전체</button>}
        <select className="field w-36" value={corpId ?? ''} onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)} aria-label="법인">
          <option value="">법인 전체</option>
          {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
        </select>
        <form className="flex gap-1" onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }}>
          <input className="field w-44" placeholder="차량번호·기사명" value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
        <span className="inline-flex items-center gap-1 text-xs">
          <input id="ins-pending" type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} />
          <label htmlFor="ins-pending">미반영 회차 있는 계약만</label>
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <button className="btn" onClick={() => setShowAcct((v) => !v)}>{showAcct ? '납부 계좌 닫기' : '납부 계좌'}</button>
          {canWrite && <button className="btn" onClick={() => setFileOpen(true)} title="경기공제조합 「분할납입 대상차량조회」 엑셀">분납자료 올리기</button>}
          {canWrite && <button className="btn-primary" onClick={() => setEntry(true)}>계약 입력</button>}
        </div>
      </div>
      {showAcct && <InsAccountPanel corps={corps} canWrite={canWrite} />}
      {err && <ErrorBox message={err} />}
      {note && <div className="card p-2 text-sm text-green-800">✓ {note}</div>}
      {canWrite && (
        <div className="flex h-10 flex-wrap items-center gap-2 px-1 text-sm">
          <span className="inline-flex items-center gap-1">
            <input id="ins-all" type="checkbox" checked={allPicked} disabled={openInst.length === 0}
                   onChange={(e) => setSel(e.target.checked ? new Set(openInst.map((i) => i.id)) : new Set())} />
            <label htmlFor="ins-all">보이는 미반영 회차 전부 고르기({openInst.length})</label>
          </span>
          <button className="btn-primary py-1" disabled={busy || picked.length === 0}
                  onClick={() => reflect({ installmentIds: picked.map((i) => i.id) }, picked.length, pickedSum)}>
            출납반영 {picked.length ? `(${picked.length}회차 · ${money(pickedSum)}원)` : ''}
          </button>
          <label className="inline-flex items-center gap-1 text-xs text-ink-soft" title="#204 — 비우면 회차마다 기한 7일 전. 신규처럼 다른 날 낼 땐 날짜를 고르세요.">
            출납 일자
            <input type="date" className="field w-36 py-0.5" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
            {payDate && <button type="button" className="underline" onClick={() => setPayDate('')}>비우기</button>}
          </label>
          <span className="text-xs text-ink-soft">출납일보 일자 = {payDate ? '고른 날짜' : '기한 − 7일'} · 시재계정 「보험료(차량)」 · 업체 = 공제조합 · memo = 납부 계좌</span>
        </div>
      )}
      {truncated && <div className="px-1 text-xs text-amber-800">1,000건까지만 보입니다 — 월·법인으로 좁혀 주세요.</div>}
      <TopScrollbar target={fitRef} />
      <div ref={fitRef} className="card overflow-auto p-0" style={{ maxHeight: fitH }}>
        <table className="w-full min-w-[980px] text-sm [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-canvas">
          <thead>
            <tr className="border-b border-line bg-canvas text-left text-xs text-ink-soft">
              <th className="w-8 px-2 py-1.5" />
              <th className="px-2">회차</th><th className="px-2" title="1회차 = 공제 시작일 · 2~4회차 = 실효예정일(기간 시작 + 30일)">기한</th><th className="px-2">기간</th>
              <th className="px-2 text-right">금액</th><th className="px-2">출납일(기한 − 7일)</th><th className="px-2">출납</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-ink-faint">계약이 없습니다{canWrite ? ' — 「계약 입력」으로 넣어 주세요' : ''}.</td></tr>}
            {rows.map((p) => {
              const open = p.installments.filter((i) => !i.reflected);
              const openSum = open.reduce((s, i) => s + Number(i.amount), 0);
              const anyReflected = p.installments.some((i) => i.reflected);
              return [
                <tr key={`p${p.id}`} className="border-t border-line bg-brand-50/40">
                  <td colSpan={7} className="px-2 py-1.5">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <b>{p.car_no}</b>
                      <span>{p.driver_name}</span>
                      <span className="text-ink-soft">{p.corp_name} · {p.union_name} · {p.kind}</span>
                      {p.contract_no && <span className="text-xs text-ink-soft">계약 {p.contract_no}</span>}
                      {p.start_date && <span className="tabular-nums">시작 {p.start_date} · 총 <b>{money(p.total_amount)}</b>원</span>}
                      {p.own_damage && <span className="text-ink-soft">자차 {p.own_damage}</span>}
                      <span className="text-xs text-ink-faint">{dash(p.created_by?.split('@')[0])} {p.created_at}</span>
                      {canWrite && (
                        <span className="ml-auto flex gap-1">
                          <button className="btn py-0.5 text-xs" disabled={busy || open.length === 0}
                                  onClick={() => reflect({ policyIds: [p.id] }, open.length, openSum)}>출납반영 ({open.length})</button>
                          <button className="btn py-0.5 text-xs text-red-700" disabled={busy || anyReflected}
                                  title={anyReflected ? '출납일보에 반영한 회차가 있어 지울 수 없습니다(출납일보에서 먼저 지우세요)' : '이 계약 지우기'}
                                  onClick={() => { if (window.confirm(`${p.car_no} ${p.start_date ?? p.contract_no ?? ''} 계약을 지울까요?`)) void act(async () => { await api.deleteInsPolicy({ id: p.id }); return '계약을 지웠습니다.'; }); }}>삭제</button>
                        </span>
                      )}
                    </div>
                  </td>
                </tr>,
                ...p.installments.map((i) => (
                  <tr key={`i${i.id}`} className={`border-t border-line/50 ${month && i.due_date.startsWith(month) ? 'bg-amber-50' : ''}`}>
                    <td className="px-2 py-1">
                      {canWrite && !i.reflected && (
                        <input type="checkbox" aria-label={`${i.seq}회차 고르기`} checked={sel.has(i.id)}
                               onChange={(e) => setSel((cur) => { const n = new Set(cur); if (e.target.checked) n.add(i.id); else n.delete(i.id); return n; })} />
                      )}
                    </td>
                    <td className="px-2">{i.seq}회차</td>
                    <td className="px-2 tabular-nums">{i.due_date}</td>
                    <td className="px-2 tabular-nums text-ink-soft">
                      {short(i.period_from)} ~ {canWrite && !i.reflected ? (
                        <input type="date" className="field h-7 w-36 py-0 text-xs" defaultValue={i.period_to} aria-label={`${i.seq}회차 기간 끝`} title="기간 끝(적요 글자) — 바꾸면 저장"
                               onBlur={(e) => { const v = e.target.value; if (v && v !== i.period_to) void act(async () => { await api.updateInsInstallment({ id: i.id, periodTo: v }); return '기간 끝을 고쳤습니다.'; }); }} />
                      ) : short(i.period_to)}
                    </td>
                    <td className="px-2 text-right tabular-nums">
                      {edit?.id === i.id ? (
                        <input className="field h-7 w-28 py-0 text-right" autoFocus inputMode="numeric" value={commaInput(edit.value)}
                               onChange={(e) => setEdit({ id: i.id, value: e.target.value.replace(/[^0-9]/g, '') })}
                               onBlur={() => saveAmount(i.id, edit.value)}
                               onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEdit(null); }} />
                      ) : canWrite && !i.reflected ? (
                        <button className="tabular-nums underline decoration-dotted" title="눌러서 금액 고치기" onClick={() => setEdit({ id: i.id, value: String(Math.round(Number(i.amount))) })}>{money(i.amount)}</button>
                      ) : money(i.amount)}
                    </td>
                    <td className="px-2 tabular-nums">{i.pay_date}</td>
                    <td className="px-2 text-xs">{statusOf(i)}</td>
                  </tr>
                )),
              ];
            })}
          </tbody>
        </table>
      </div>
      {fileOpen && (
        <Modal width="max-w-7xl">
          <InsFileForm corps={corps} onClose={() => setFileOpen(false)} onDone={(m) => { setFileOpen(false); setNote(m); void load(); }} />
        </Modal>
      )}
      {entry && (
        <Modal width="max-w-7xl">
          <InsEntryForm corps={corps} onClose={() => setEntry(false)}
                        onSaved={(n) => { setEntry(false); setNote(`${n}건 저장했습니다 — 분납 4회가 만들어졌습니다.`); void load(); }} />
        </Modal>
      )}
    </div>
  );
}
